import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Badge,
  makeStyles,
  tokens,
} from '@fluentui/react-components';

import { EXTERNAL_DECISION_STATUS, TASK_STATUS, TASK_TYPE, TChangePlan } from '../../models';
import { activeTasks } from '../../utils';
import { DefinitionList } from '../shared';

const useStyles = makeStyles({
  root: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: 'hidden',
  },
  badges: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
});

// Compact expandable roll-ups shown below the task list, only when relevant
// (prompt Part 8) - never large separate dashboards.
export const ContextualRollups = ({ plan }: { plan: TChangePlan }) => {
  const classes = useStyles();
  const tasks = activeTasks(plan);

  const documents = tasks.filter((task) => task.taskType === TASK_TYPE.DOCUMENT_REVISION);
  const external = tasks.filter((task) => task.external);
  const training = tasks.filter((task) => task.taskType === TASK_TYPE.TRAINING_REQUIREMENT);
  const regulatory = tasks.filter((task) => task.taskType === TASK_TYPE.REGULATORY_ASSESSMENT);

  const hasDocuments = documents.length > 0;
  const hasRegulatory = external.length > 0 || regulatory.length > 0;
  const hasTraining = training.length > 0;

  if (!hasDocuments && !hasRegulatory && !hasTraining) return null;

  const docDraft = documents.filter((task) => task.status === TASK_STATUS.NOT_STARTED).length;
  const docReview = documents.filter((task) => task.status === TASK_STATUS.IN_REVIEW).length;
  const docApproved = documents.filter((task) => task.status === TASK_STATUS.COMPLETE).length;

  const externalTask = external[0];

  return (
    <div className={classes.root}>
      <Accordion multiple collapsible>
        {hasDocuments ? (
          <AccordionItem value="documents">
            <AccordionHeader>Documents ({documents.length})</AccordionHeader>
            <AccordionPanel>
              <div className={classes.badges}>
                <Badge appearance="tint" color="informative">
                  Draft {docDraft}
                </Badge>
                <Badge appearance="tint" color="warning">
                  In review {docReview}
                </Badge>
                <Badge appearance="tint" color="success">
                  Approved {docApproved}
                </Badge>
              </div>
            </AccordionPanel>
          </AccordionItem>
        ) : null}

        {hasRegulatory ? (
          <AccordionItem value="regulatory">
            <AccordionHeader>Regulatory and external decisions</AccordionHeader>
            <AccordionPanel>
              <DefinitionList
                items={[
                  {
                    label: 'Assessment status',
                    value:
                      regulatory.length > 0
                        ? regulatory.every((task) => task.status === TASK_STATUS.COMPLETE)
                          ? 'Complete'
                          : 'In progress'
                        : 'Not applicable',
                  },
                  { label: 'External owner', value: externalTask?.owner || 'Regulatory affairs' },
                  {
                    label: 'Decision status',
                    value: externalTask?.externalStatus ?? EXTERNAL_DECISION_STATUS.NOT_STARTED,
                  },
                  { label: 'Required before release', value: externalTask?.releaseBlocking ? 'Yes' : 'No' },
                ]}
              />
            </AccordionPanel>
          </AccordionItem>
        ) : null}

        {hasTraining ? (
          <AccordionItem value="training">
            <AccordionHeader>Training</AccordionHeader>
            <AccordionPanel>
              <DefinitionList
                items={[
                  { label: 'Requirements identified', value: String(training.length) },
                  { label: 'Assignments', value: 'Not released until the relevant document is approved' },
                  {
                    label: 'Complete / pending',
                    value: `${training.filter((task) => task.status === TASK_STATUS.COMPLETE).length} / ${
                      training.filter((task) => task.status !== TASK_STATUS.COMPLETE).length
                    }`,
                  },
                ]}
              />
            </AccordionPanel>
          </AccordionItem>
        ) : null}
      </Accordion>
    </div>
  );
};
